/**
 * @author        yontaek@zumper.com (Yon Taek Chung)
 * @copyright     Copyright (c) 2023, Zumper
 * @description  Ratings and Reviews meta reducer
 */

import type { RatingsAndReviewsMetaType } from '@zcode/state-type'
import type { Values } from '@zcode/utility-types'
import { combineReducers } from 'redux'
import { type ExtractActions, handleActions } from 'redux-actions'

import type * as ratingsAndReviewsActions from '../actions'
import {
  CLEAR_MODAL_RATING_AND_REVIEWS_COUNTS_DATA,
  CLEAR_MODAL_RATING_AND_REVIEWS_DATA,
  CLEAR_RATING_AND_REVIEWS_DATA,
  DONE_FETCHING_MODAL_RATING_AND_REVIEWS_COUNTS_DATA,
  DONE_FETCHING_MODAL_RATING_AND_REVIEWS_DATA,
  DONE_FETCHING_RATING_AND_REVIEWS_DATA,
  ERROR_FETCHING_MODAL_RATING_AND_REVIEWS_COUNTS_DATA,
  ERROR_FETCHING_MODAL_RATING_AND_REVIEWS_DATA,
  ERROR_FETCHING_RATING_AND_REVIEWS_DATA,
  START_FETCHING_MODAL_RATING_AND_REVIEWS_COUNTS_DATA,
  START_FETCHING_MODAL_RATING_AND_REVIEWS_DATA,
  START_FETCHING_RATING_AND_REVIEWS_DATA,
} from '../constants/index'

type ActionTypes = ExtractActions<Values<typeof ratingsAndReviewsActions>>

export const metaReducer = combineReducers<RatingsAndReviewsMetaType, ActionTypes>({
  error: handleActions(
    {
      [ERROR_FETCHING_RATING_AND_REVIEWS_DATA]: (state, { payload }) => payload?.error || state,
      [CLEAR_RATING_AND_REVIEWS_DATA]: () => null,
      [START_FETCHING_RATING_AND_REVIEWS_DATA]: () => null,
    },
    null as RatingsAndReviewsMetaType['error']
  ),
  fetched: handleActions(
    {
      [START_FETCHING_RATING_AND_REVIEWS_DATA]: () => false,
      [CLEAR_RATING_AND_REVIEWS_DATA]: () => false,
      [DONE_FETCHING_RATING_AND_REVIEWS_DATA]: () => true,
    },
    false as RatingsAndReviewsMetaType['fetched']
  ),
})

export const modalMetaReducer = combineReducers<RatingsAndReviewsMetaType, ActionTypes>({
  error: handleActions(
    {
      [ERROR_FETCHING_MODAL_RATING_AND_REVIEWS_DATA]: (state, { payload }) =>
        payload?.error || state,
      [CLEAR_MODAL_RATING_AND_REVIEWS_DATA]: () => null,
      [START_FETCHING_MODAL_RATING_AND_REVIEWS_DATA]: () => null,
    },
    null as RatingsAndReviewsMetaType['error']
  ),
  fetched: handleActions(
    {
      [START_FETCHING_MODAL_RATING_AND_REVIEWS_DATA]: () => false,
      [CLEAR_MODAL_RATING_AND_REVIEWS_DATA]: () => false,
      [DONE_FETCHING_MODAL_RATING_AND_REVIEWS_DATA]: () => true,
    },
    false as RatingsAndReviewsMetaType['fetched']
  ),
})

export const modalCountsMetaReducer = combineReducers<RatingsAndReviewsMetaType, ActionTypes>({
  error: handleActions(
    {
      [ERROR_FETCHING_MODAL_RATING_AND_REVIEWS_COUNTS_DATA]: (state, { payload }) =>
        payload?.error || state,
      [CLEAR_MODAL_RATING_AND_REVIEWS_COUNTS_DATA]: () => null,
      [START_FETCHING_MODAL_RATING_AND_REVIEWS_DATA]: () => null,
    },
    null as RatingsAndReviewsMetaType['error']
  ),
  fetched: handleActions(
    {
      [START_FETCHING_MODAL_RATING_AND_REVIEWS_COUNTS_DATA]: () => false,
      [CLEAR_MODAL_RATING_AND_REVIEWS_COUNTS_DATA]: () => false,
      [DONE_FETCHING_MODAL_RATING_AND_REVIEWS_COUNTS_DATA]: () => true,
    },
    false as RatingsAndReviewsMetaType['fetched']
  ),
})
